/* ============================================================================
   The drawing set as a book — full screen, one sheet per spread, turned with
   the arrows, the keyboard or a click on either edge. A filmstrip along the
   bottom keeps the whole set in reach, and any spread can be handed to the
   full-quality viewer for zooming and notes. Built for the combined multi-
   sheet set a client wants to LEAF THROUGH rather than open sheet by sheet.
   ========================================================================== */
import { useEffect, useMemo, useState } from "react";
import { Icon } from "./Icons";

export interface BookSheet {
  id: string;
  code: string;
  title: string;
  discipline?: string;
  phase?: string;
  revision?: string;
  previewUrl?: string;
  fileUrl?: string;
}

export function BookViewer({ sheets, startId, title, onInspect, onClose }: {
  sheets: BookSheet[];
  startId?: string;
  title: string;
  /** Hand one sheet to the full-quality viewer (zoom + notes). */
  onInspect?: (s: BookSheet) => void;
  onClose: () => void;
}) {
  const pages = useMemo(() => sheets.filter((s) => s.previewUrl || s.fileUrl), [sheets]);
  const [i, setI] = useState(() => Math.max(0, pages.findIndex((s) => s.id === startId)));
  const [idle, setIdle] = useState(false);
  const cur = pages[i];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" || e.key === " ") setI((x) => Math.min(pages.length - 1, x + 1));
      if (e.key === "ArrowLeft") setI((x) => Math.max(0, x - 1));
      if (e.key === "Home") setI(0);
      if (e.key === "End") setI(pages.length - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pages.length, onClose]);

  /* The chrome fades while you read; any movement brings it back. */
  useEffect(() => {
    let t: ReturnType<typeof setTimeout>;
    const wake = () => { setIdle(false); clearTimeout(t); t = setTimeout(() => setIdle(true), 2600); };
    wake();
    window.addEventListener("pointermove", wake);
    return () => { clearTimeout(t); window.removeEventListener("pointermove", wake); };
  }, []);

  // warm the neighbours so a page-turn never shows a blank spread
  useEffect(() => {
    [pages[i + 1], pages[i - 1]].forEach((s) => { if (s?.previewUrl) { const im = new Image(); im.src = s.previewUrl; } });
  }, [i, pages]);

  if (!pages.length) return null;
  const chrome: React.CSSProperties = { transition: "opacity .4s ease", opacity: idle ? 0 : 1, pointerEvents: idle ? "none" : "auto" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "#171410", display: "flex", flexDirection: "column" }}>
      {/* top bar */}
      <div className="row between" style={{ padding: "12px 18px", color: "#efe9dd", ...chrome }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", opacity: 0.6 }}>{title}</div>
          <div className="row wrap" style={{ gap: 8, alignItems: "baseline", marginTop: 2 }}>
            <span className="mono" style={{ fontSize: 12.5, opacity: 0.85 }}>{cur.code}{cur.revision ? ` · ${cur.revision}` : ""}</span>
            <span style={{ fontSize: 14.5, fontWeight: 600 }}>{cur.title}</span>
            {cur.phase && <span style={{ fontSize: 10.5, opacity: 0.55 }}>{cur.phase}</span>}
          </div>
        </div>
        <div className="row" style={{ gap: 8, flexShrink: 0 }}>
          {onInspect && (
            <button onClick={() => onInspect(cur)} style={{ background: "rgba(255,255,255,.1)", border: "1px solid rgba(255,255,255,.22)", color: "#efe9dd", borderRadius: 9, padding: "7px 12px", fontSize: 12, cursor: "pointer" }}>
              <Icon.edit width={12} style={{ verticalAlign: -2, marginRight: 5 }} />Zoom &amp; note this sheet
            </button>
          )}
          <button onClick={onClose} aria-label="Close" style={{ background: "rgba(255,255,255,.1)", border: "1px solid rgba(255,255,255,.22)", color: "#efe9dd", borderRadius: 9, width: 32, height: 32, cursor: "pointer", display: "grid", placeItems: "center" }}>
            <Icon.close width={14} />
          </button>
        </div>
      </div>

      {/* the spread */}
      <div style={{ flex: 1, position: "relative", display: "grid", placeItems: "center", padding: "0 64px", minHeight: 0 }}>
        {cur.previewUrl
          ? <img key={cur.id} src={cur.previewUrl} alt={`${cur.code} — ${cur.title}`}
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", background: "#fff", borderRadius: 4, boxShadow: "0 18px 60px rgba(0,0,0,.5)" }} />
          : <a href={cur.fileUrl} target="_blank" rel="noreferrer" style={{ color: "#efe9dd", fontSize: 14 }}>No preview rendered — open {cur.code} directly.</a>}
        {/* page-turn edges */}
        {i > 0 && (
          <button onClick={() => setI(i - 1)} aria-label="Previous sheet"
            style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 64, background: "transparent", border: "none", cursor: "pointer", color: "#efe9dd", ...chrome }}>
            <span style={{ display: "grid", placeItems: "center", width: 38, height: 38, margin: "0 auto", borderRadius: 38, background: "rgba(255,255,255,.12)" }}><Icon.caretDown width={18} style={{ transform: "rotate(90deg)" }} /></span>
          </button>
        )}
        {i < pages.length - 1 && (
          <button onClick={() => setI(i + 1)} aria-label="Next sheet"
            style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 64, background: "transparent", border: "none", cursor: "pointer", color: "#efe9dd", ...chrome }}>
            <span style={{ display: "grid", placeItems: "center", width: 38, height: 38, margin: "0 auto", borderRadius: 38, background: "rgba(255,255,255,.12)" }}><Icon.caretDown width={18} style={{ transform: "rotate(-90deg)" }} /></span>
          </button>
        )}
      </div>

      {/* filmstrip + counter */}
      <div style={{ padding: "10px 18px 14px", ...chrome }}>
        <div className="row" style={{ gap: 10, alignItems: "center" }}>
          <span className="mono" style={{ color: "#efe9dd", fontSize: 12, opacity: 0.75, flexShrink: 0 }}>{i + 1} / {pages.length}</span>
          <div style={{ flex: 1, display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4 }}>
            {pages.map((s, j) => (
              <button key={s.id} onClick={() => setI(j)} title={`${s.code} — ${s.title}`}
                style={{ flexShrink: 0, width: 64, height: 46, padding: 0, borderRadius: 5, overflow: "hidden", cursor: "pointer", background: "#fff", border: j === i ? "2px solid var(--accent, #c96f4a)" : "2px solid transparent", opacity: j === i ? 1 : 0.55 }}>
                {s.previewUrl
                  ? <img src={s.previewUrl} alt={s.code} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  : <span className="mono" style={{ fontSize: 8.5, display: "grid", placeItems: "center", height: "100%" }}>{s.code}</span>}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
